import { Button } from '@uroute/ui';
import { useState } from 'react';
import { useAuth } from '../lib/auth';
import { isIosSafari, useInstall } from '../lib/install';
import { usePrefs, setPrefs } from '../lib/prefs';
import { useQueue } from '../lib/queue';
import { useNotifications } from '../lib/notifications';
import { initials } from '../lib/format';
import { Download, LogOut, ShieldCheck, Bell, RefreshCw, UserIcon } from '../components/icons';

function ToggleRow({
  label,
  hint,
  checked,
  onChange,
  testId,
}: {
  label: string;
  hint?: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  testId: string;
}) {
  return (
    <label className="togglerow" data-testid={testId}>
      <span className="togglerow__text">
        <span className="togglerow__label">{label}</span>
        {hint ? <span className="togglerow__hint">{hint}</span> : null}
      </span>
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="togglerow__input"
      />
      <span className={`togglerow__track${checked ? ' togglerow__track--on' : ''}`} aria-hidden="true">
        <span className="togglerow__thumb" />
      </span>
    </label>
  );
}

export function ProfilePage() {
  const { user, logout } = useAuth();
  const prefs = usePrefs();
  const queue = useQueue();
  const notifs = useNotifications();
  const { canInstall, installed, promptInstall } = useInstall();
  const [iosHint, setIosHint] = useState(false);
  const ios = isIosSafari();

  const pending = queue.pending + queue.failed;

  return (
    <div className="profile" data-testid="profile">
      <section className="profile__card">
        <span className="profile__avatar" aria-hidden="true">
          {user ? initials(user.name) : '?'}
        </span>
        <div className="profile__id">
          <h1 className="profile__name">{user?.name ?? 'Driver'}</h1>
          <p className="profile__email u-data">{user?.email ?? ''}</p>
        </div>
      </section>

      <h2 className="section-label">Preferences</h2>
      <section className="panel" data-testid="profile-prefs">
        <ToggleRow
          label="On duty"
          hint="Show me as available for new assignments on this device"
          checked={prefs.onDuty}
          onChange={(v) => setPrefs({ onDuty: v })}
          testId="pref-duty"
        />
        <ToggleRow
          label="Assignment alerts"
          hint="Notify me when a new job is assigned"
          checked={prefs.notifyAssignments}
          onChange={(v) => setPrefs({ notifyAssignments: v })}
          testId="pref-assignments"
        />
        <ToggleRow
          label="Message alerts"
          hint="Notify me about dispatch messages"
          checked={prefs.notifyMessages}
          onChange={(v) => setPrefs({ notifyMessages: v })}
          testId="pref-messages"
        />
      </section>

      <h2 className="section-label">Notifications</h2>
      <section className="panel" data-testid="profile-notif-perm">
        <div className="permrow">
          <Bell size={18} />
          <div className="permrow__text">
            <span className="permrow__title">Browser notifications</span>
            <span className="permrow__hint">
              {typeof Notification !== 'undefined' && Notification.permission === 'granted'
                ? 'Enabled — new assignments will ping this device.'
                : 'Enable to get assignment pings even when the app is in the background.'}
            </span>
          </div>
          {typeof Notification !== 'undefined' && Notification.permission !== 'granted' ? (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => void notifs.refresh().then(() => import('../lib/notifications').then((m) => m.requestNotificationPermission()))}
              data-testid="enable-notifications"
            >
              Enable
            </Button>
          ) : (
            <ShieldCheck size={18} className="permrow__ok" aria-label="Enabled" />
          )}
        </div>
      </section>

      <h2 className="section-label">Sync</h2>
      <section className="panel" data-testid="profile-sync">
        <div className="permrow">
          <RefreshCw size={18} />
          <div className="permrow__text">
            <span className="permrow__title">Offline queue</span>
            <span className="permrow__hint">
              {pending === 0
                ? 'Everything is synced.'
                : `${pending} change${pending === 1 ? '' : 's'} waiting to sync.`}
            </span>
          </div>
          {queue.syncing ? <span className="permrow__badge">Syncing…</span> : null}
        </div>
      </section>

      {(canInstall || ios) && !installed ? (
        <>
          <Button
            variant="secondary"
            onClick={() => {
              if (ios) setIosHint(true);
              else void promptInstall();
            }}
            data-testid="install-button"
            className="profile__install"
          >
            <Download size={16} />
            {ios ? 'Add UR-ROUTE to the home screen' : 'Install app on this phone'}
          </Button>
          {ios && iosHint ? (
            <p className="notice notice--info" role="status" data-testid="install-hint">
              Open UR-ROUTE in Safari, tap the Share button, then choose &ldquo;Add to Home
              Screen&rdquo;.
            </p>
          ) : null}
        </>
      ) : null}

      <Button
        variant="ghost"
        onClick={() => void logout()}
        data-testid="profile-signout"
        className="profile__signout"
      >
        <LogOut size={16} />
        Sign out
      </Button>

      <p className="profile__version u-data">
        <UserIcon size={12} /> UR-ROUTE Driver · v{__APP_VERSION__}
      </p>
    </div>
  );
}
